<template>
  <div class="progress-container mt-4">
    <!-- Header con información del archivo -->
    <div v-if="file" class="file-header mb-3">
      <div class="file-header-content">
        <i class="fa fa-file-pdf-o text-primary"></i>
        <div class="file-details">
          <h6 class="file-name">{{ file.name }}</h6>
          <small class="file-info">
            {{ (file.size / 1024 / 1024).toFixed(2) }} MB • {{ file.type }}
          </small>
        </div>
      </div>
    </div>

    <div class="progress-info-content">
      <small class="text-muted">
        <i class="fa fa-clock-o mr-1"></i>
        Tiempo transcurrido: {{ elapsedTime }}
        <span class="mx-2">|</span>
        <i class="fa fa-hourglass-half mr-1"></i>
        Estimado: {{ estimatedTime }}s
      </small>
    </div>
    
    <!-- progreso -->
     <div class="">
      <!-- Texto del progreso -->
        <div class="progress-text-container">
          <p class="progress-text">
            {{ text }}
          </p>
          <span class="progress-percentage" :class="{ 'percentage-complete': percentage >= 100 }">
            {{ percentage }}%
          </span>
      </div>

      <!-- Barra de progreso principal -->
      <div class="progress-bar">
        <div 
          class="progress-fill" 
          :style="{ width: percentage + '%' }"
          :class="{ 'progress-complete': percentage >= 100 }"
        ></div>
        <div class="progress-glow" :style="{ width: percentage + '%' }"></div>
      </div>
    </div>
    
    <!-- Barra de progreso secundaria para pasos -->
    <div v-if="steps && steps.length > 0" class="progress-steps mt-3">
      <ProcessingStep
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :subtitle="step.subtitle"
        :icon="step.icon"
        :isActive="step.isActive"
        :isCompleted="step.isCompleted"
      />
    </div>

  </div>
</template>

<script>

// Componentes
import ProcessingStep from './ProcessingStep.vue';

export default {
  name: 'ProgressBar',
  
  components: {
    ProcessingStep
  },

  props: {
    percentage: {
      type: Number,
      default: 0,
      validator: value => value >= 0 && value <= 100
    },
    text: {
      type: String,
      default: 'Procesando...'
    },
    steps: {
      type: Array,
      default: () => []
    },
    file: {
      type: [File],
      default: null
    },
    elapsedTime: {
      type: [Number, String],
      default: 0
    },
    estimatedTime: {
      type: [Number, String],
      default: 0
    },
    progressRatio: {
      type: [Number, String],
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    progressRatio: {
      handler(newRatio) {
        this.updateStepsBasedOnRatio(newRatio);
      },
      immediate: true
    },
    percentage: {
      handler(newPercentage) {
        this.updateStepsBasedOnPercentage(newPercentage);
      },
      immediate: true
    },
    loading: {
      handler(newLoading) {
        if (!newLoading && this.percentage >= 100) {
          this.completeAllSteps();
        }
      },
      immediate: true
    }
  },
  methods: {
    updateStepsBasedOnRatio(ratio) {
      if (!this.steps || this.steps.length === 0) return;
      
      // Si no está cargando y el progreso es 100%, completar todos los pasos
      if (!this.loading && this.percentage >= 100) {
        this.completeAllSteps();
        return;
      }
      
      // Resetear todos los pasos
      this.steps.forEach((step) => {
        step.isActive = false;
        step.isCompleted = false;
      });
      
      // Lógica de activación basada en el ratio con transiciones más suaves
      if (ratio >= 0 && ratio < 0.25) {
        // Primer paso activo (0-25%)
        if (this.steps[0]) {
          this.steps[0].isActive = true;
        }
      } else if (ratio >= 0.25 && ratio < 0.75) {
        // Primer paso completado, segundo activo (25-75%)
        if (this.steps[0]) this.steps[0].isCompleted = true;
        if (this.steps[1]) this.steps[1].isActive = true;
      } else if (ratio >= 0.75 && ratio < 0.95) {
        // Primeros dos pasos completados, tercero activo (75-95%)
        if (this.steps[0]) this.steps[0].isCompleted = true;
        if (this.steps[1]) this.steps[1].isCompleted = true;
        if (this.steps[2]) this.steps[2].isActive = true;
      } else if (ratio >= 0.95) {
        // Todos los pasos completados (95-100%)
        this.steps.forEach((step) => {
          step.isCompleted = true;
          step.isActive = false;
        });
      }
    },

    updateStepsBasedOnPercentage(percentage) {
      if (!this.steps || this.steps.length === 0) return;
      
      // Lógica alternativa basada en porcentaje para mayor precisión
      const ratio = percentage / 100;
      this.updateStepsBasedOnRatio(ratio);
    },

    // Método para actualizar pasos manualmente desde el componente padre
    updateStepStatus(stepIndex, isActive, isCompleted) {
      if (this.steps && this.steps[stepIndex]) {
        this.steps[stepIndex].isActive = isActive;
        this.steps[stepIndex].isCompleted = isCompleted;
      }
    },

    // Método para resetear todos los pasos
    resetSteps() {
      if (this.steps) {
        this.steps.forEach((step, index) => {
          step.isActive = index === 0;
          step.isCompleted = false;
        });
      }
    },

    completeAllSteps() {
      if (this.steps) {
        this.steps.forEach((step) => {
          step.isCompleted = true;
          step.isActive = false;
        });
      }
    }
  }
}
</script>

<style scoped>
/* File Header Styles */
.file-header {
  padding: 1rem;
  background: rgba(0, 123, 255, 0.1);
  border: 1px solid rgba(0, 123, 255, 0.2);
  border-radius: 8px;
}

.file-header-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.file-header i {
  font-size: 2rem;
  flex-shrink: 0;
}

.file-details {
  flex: 1;
}

.file-name {
  margin: 0 0 0.25rem 0;
  color: #007bff;
  font-weight: 600;
  font-size: 1rem;
  word-break: break-word;
}

.file-info {
  color: #6c757d;
  font-size: 0.85rem;
}

/* Progress Container Styles */
.progress-container {
  background: #ffffff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e9ecef;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #f8f9fa;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1rem;
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  border-radius: 6px;
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 2;
}

.progress-fill.progress-complete {
  background: linear-gradient(90deg, #28a745, #20c997);
  animation: progressComplete 0.5s ease-in-out;
}

.progress-glow {
  height: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.3), transparent);
  border-radius: 6px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  animation: progressGlow 2s ease-in-out infinite;
}

@keyframes progressComplete {
  0% { transform: scaleX(1); }
  50% { transform: scaleX(1.05); }
  100% { transform: scaleX(1); }
}

@keyframes progressGlow {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 0.6; }
}

.progress-text-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.progress-text {
  margin: 0;
  color: #495057;
  font-size: 0.95rem;
  font-weight: 500;
}

.progress-percentage {
  font-weight: 700;
  color: #007bff;
  font-size: 1.1rem;
  transition: color 0.3s ease;
}

.progress-percentage.percentage-complete {
  color: #28a745;
}

.progress-status {
  margin-bottom: 1rem;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-processing {
  background: rgba(0, 123, 255, 0.1);
  color: #007bff;
}

.status-success {
  background: rgba(40, 167, 69, 0.1);
  color: #28a745;
}

.status-error {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.status-warning {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.status-indicator i {
  font-size: 1rem;
}

/* Progress Steps */
.progress-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.step-pending {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  opacity: 0.6;
}

.step-active {
  background: rgba(0, 123, 255, 0.1);
  border: 2px solid #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
}

.step-completed {
  background: rgba(40, 167, 69, 0.1);
  border: 2px solid #28a745;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.step-pending .step-icon {
  background: #e9ecef;
  color: #6c757d;
}

.step-active .step-icon {
  background: #007bff;
  color: white;
  animation: stepPulse 2s infinite;
}

.step-completed .step-icon {
  background: #28a745;
  color: white;
}

@keyframes stepPulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

.step-info {
  flex: 1;
}

.step-title {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
}

.step-subtitle {
  font-size: 0.85rem;
  color: #6c757d;
}

/* Responsive */
@media (max-width: 768px) {
  .progress-container {
    padding: 1rem;
  }
  
  .progress-text-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .step-indicator {
    padding: 0.75rem;
  }
  
  .step-icon {
    width: 32px;
    height: 32px;
    font-size: 1rem;
  }
}

/* Action Buttons */
.action-buttons {
  text-align: center;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.action-buttons .btn {
  padding: 0.75rem 2rem;
  font-weight: 600;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.action-buttons .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.action-buttons .btn i {
  font-size: 1.1rem;
}
</style> 